import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import classNames from 'classnames/bind';

import commonStyles from 'styles/common.pcss';
import pushEvent, { EVENT_444_PHONE_VALIDATION } from 'pushEvent';
import { unformatPhone } from 'utils/formatting';

import NumberForm from './components/Forms/NumberForm';
import SMSForm from './components/Forms/SMSForm';
import { Authenticated, Error } from './components/Forms/Statuses';
import SMSCaptchaForm from './components/Forms/SMSCaptchaForm';
import styles from './styles.pcss';

const cx = classNames.bind({ ...commonStyles, ...styles });

/**
 * A complex multi-step form for authentication
 * used either in AuthModal or CatalogBanner
 */
class AuthForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ctn: props.ctn || '',
      step: props.isAuthenticated ? this.STEPS.auth : this.STEPS.ctn,
      isPending: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.shown && nextProps.shown) {
      document.addEventListener('click', this.handleOutsideClick, true);
      document.addEventListener('touchstart', this.handleOutsideClick, true);
    }

    if (this.props.shown && !nextProps.shown) {
      document.removeEventListener('click', this.handleOutsideClick, false);
      document.removeEventListener('touchstart', this.handleOutsideClick, false);
    }
  }

  STEPS = {
    ctn: 'ctn',
    smsCode: 'code',
    needCaptcha: 'captcha',
    auth: 'auth',
    error: 'error',
  };

  ERRORS = {
    authMaxTokensByPeriod: 'smsCodeError',
    otherError: 'otherError',
  };

  fetchLoginModel = () => {
    const { mainSiteUrl, redirectUrl } = this.props;
    const { ctn } = this.state;

    axios({
      method: 'GET',
      url:
        ctn ?
          `${mainSiteUrl}/menu/loginmodel/?CTN=${unformatPhone(ctn)}&returnUrl=${redirectUrl}`
        : `${mainSiteUrl}/menu/loginmodel/`,
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      if (response.status === 200) {
        this.setState({
          loginModel: response.data,
        });
      }
    });
  };

  requestSMS = (captcha) => {
    const { onRedirect } = this.props;
    const { ctn } = this.state;

    const preRequest = (callback) => {
      axios({
        method: 'GET',
        url: '/menu/loginmodel/',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        if (response.status === 200) {
          this.setState({
            loginModel: response.data,
          });
          callback();
        }
      });
    };

    preRequest(() => {
      const responseBisUrl = (this.state.loginModel || {}).bisLoginUrl || '';
      let url = `${responseBisUrl}/identity/generateonetimepassword?login=${unformatPhone(ctn)}`;
      if (captcha !== undefined) {
        url = `${url}&captcha=${captcha.captcha}&captchaKey=${captcha.captchaKey}`;
      }

      axios
        .get(url)
        .then((response) => {
          // Абонент принадлежит сети Билайн, код отправлен
          if (response.data && response.data.Status === 'OK') {
            pushEvent(EVENT_444_PHONE_VALIDATION, { payload: 'success' });
            this.setState(
              {
                isPending: false,
                step: this.STEPS.smsCode,
              },
              this.fetchLoginModel,
            );
          } else {
            pushEvent(EVENT_444_PHONE_VALIDATION, { payload: 'error' });
            this.setCookie(ctn);
            onRedirect();
          }
        })
        .catch(({ response }) => {
          if (response && response.data) {
            let errorType = '';
            switch (response.data.ErrorCode) {
              case 'NEED_CAPTCH':
              case 'ERROR_CAPTCH':
                this.setState({
                  step: this.STEPS.needCaptcha,
                  isPending: false,
                });
                break;
              case 'AUTH_MAX_TOKENS_BY_PERIOD':
                errorType = this.ERRORS.authMaxTokensByPeriod;
                break;
              case 'CTN_NOT_FOUND':
                pushEvent(EVENT_444_PHONE_VALIDATION, { payload: 'error' });
                this.setCookie(ctn);
                errorType = this.ERRORS.otherError;
                break;
              default:
                errorType = this.ERRORS.otherError;
            }
            if (errorType !== '') {
              this.setState({
                step: this.STEPS.error,
                error: errorType,
                isPending: false,
              });
            }
          } else {
            this.setState({
              step: this.STEPS.error,
              error: this.ERRORS.otherError,
              isPending: false,
            });
          }
        });
    });
  };

  handleNumberSubmit = ({ number }) => {
    this.setState(
      {
        ctn: number,
        isPending: true,
      },
      this.requestSMS,
    );
  };

  handleNumberReset = () => {
    this.setState({
      ctn: '',
      step: this.STEPS.ctn,
    });
  };

  setCookie = (ctn) => {
    if (document && window) {
      let domain = window.location.hostname;
      domain = domain.substring(domain.indexOf('.'));
      document.cookie = `non.beeline.ctn=${unformatPhone(ctn)};domain=${domain};path=/`;
    }
  };

  getHiddenFields = () => {
    const { loginModel, ctn } = this.state;

    if (loginModel) {
      return {
        login: unformatPhone(ctn),
        client_id: loginModel.clientId || 'quantumartapp',
        redirect_uri: loginModel.returnUrl || 'https://www.beeline.ru/logincallback',
        response_type: 'id_token',
        response_mode: 'form_post',
        state: loginModel.state,
        nonce: loginModel.nonce,
        scope: 'openid selfservice_identity usss_token profile',
        remember_me: false,
        one_time_password: true,
      };
    }

    return null;
  };

  handleOutsideClick = (event) => {
    const { shown, onClose } = this.props;
    if (shown && !this.wrapper.contains(event.target)) {
      setTimeout(onClose, 200);
    }
  };

  render() {
    const {
      children,
      numberFormDescription,
      smsFormDescription,
      smsCaptchaFormDescription,
      onRedirect,
      numberButton,
      smsCodeButton,
      captchaButton,
      authButton,
      setFocus,
      shown,
      errorTexts,
      className,
      isCompact,
      isLight,
    } = this.props;
    const { ctn, step, isPending, error } = this.state;
    const responseBisUrl = (this.state.loginModel || {}).bisLoginUrl || '';

    const _step = this.props.isAuthenticated ? this.STEPS.auth : step;
    return (
      <div
        ref={(element) => {
          this.wrapper = element;
        }}
        className={cx('container', { shown, light: isLight }, className)}
      >
        {children}
        <div className={cx('content')}>
          {_step === this.STEPS.ctn ?
            <NumberForm
              {...{ setFocus, isPending, isCompact, isLight }}
              buttonText={numberButton}
              description={numberFormDescription}
              onSubmit={this.handleNumberSubmit}
              bisUrl={responseBisUrl}
            />
          : null}
          {_step === this.STEPS.smsCode ?
            <SMSForm
              {...{ ctn, isPending, isCompact, isLight }}
              buttonText={smsCodeButton}
              description={smsFormDescription}
              onReset={this.handleNumberReset}
              onSMSRequest={this.requestSMS}
              loginModel={this.getHiddenFields()}
              bisUrl={responseBisUrl}
              onSubmit={this.requestSMS}
            />
          : null}
          {_step === this.STEPS.needCaptcha ?
            <SMSCaptchaForm
              {...{ ctn, isPending, isCompact, isLight }}
              description={smsCaptchaFormDescription}
              buttonText={captchaButton}
              onSubmit={this.requestSMS}
              onReset={this.handleNumberReset}
              bisUrl={responseBisUrl}
            />
          : null}
          {_step === this.STEPS.auth ?
            <Authenticated ctn={ctn} buttonText={authButton} onClick={onRedirect} />
          : null}
          {_step === this.STEPS.error ?
            <Error ctn={ctn} text={errorTexts[error]} onReset={this.handleNumberReset} />
          : null}
        </div>
      </div>
    );
  }
}

AuthForm.propTypes = {
  children: PropTypes.node,
  shown: PropTypes.bool,
  setFocus: PropTypes.bool,
  isCompact: PropTypes.bool,
  isLight: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  ctn: PropTypes.string,
  bisUrl: PropTypes.string,
  className: PropTypes.string,
  mainSiteUrl: PropTypes.string,
  smsFormDescription: PropTypes.string,
  numberFormDescription: PropTypes.string,
  smsCaptchaFormDescription: PropTypes.string,
  authButton: PropTypes.string,
  numberButton: PropTypes.string,
  smsCodeButton: PropTypes.string,
  captchaButton: PropTypes.string,
  errorTexts: PropTypes.object,
  onClose: PropTypes.func,
  onRedirect: PropTypes.func,
  redirectUrl: PropTypes.string,
};

export default AuthForm;
